import { Button } from '@neo/primitives/button'
import { Popover, PopoverContent, PopoverTrigger } from '@neo/primitives/popover'
import { Tooltip } from '@neo/primitives/tooltip'
import { Typography } from '@neo/primitives/typography'
import { Hash, Mic, MicOff, Phone, PhoneIncoming, PhoneOff } from 'lucide-react'
import { useState } from 'react'
import { IVRKeypad } from 'v2/pages/flow/components/ivr-dialer'
import { ElapsedTimer } from 'v2/shared/components/elapsed-timer'
import { usePhoneStore } from 'v2/state/phone'
import { CallDirection } from 'v2/types/call'
import { phoneSDK } from 'v2/utils/phone-sdk'

export const CallControls = () => {
  const { isMuted, activeCall, isCallActive } = usePhoneStore()
  const [isDialpadOpen, setIsDialpadOpen] = useState(false)

  const handleKeySelect = (key: string) => {
    phoneSDK.sendDTMF(key)
    setIsDialpadOpen(false)
  }

  // Determine if incoming call is ringing
  const isIncomingCallRinging = !isCallActive && activeCall?.direction !== CallDirection.OUTBOUND

  // Show answer/reject controls when incoming call is ringing
  if (isIncomingCallRinging) {
    return (
      <div className="border-t border-border bg-card px-4 py-3 font-inter">
        <div className="flex items-center justify-center gap-4">
          <Button
            onClick={phoneSDK.rejectCall}
            className="w-32"
            variant="destructive-outline"
            size="sm"
            data-testid="button-reject-call"
          >
            <Phone className="h-4 w-4 fill-current rotate-[135deg] mr-2 stroke-none" />
            Reject
          </Button>
          <Button
            onClick={phoneSDK.answerCall}
            className="w-32"
            variant="success"
            size="sm"
            data-testid="button-accept-call"
          >
            <PhoneIncoming className="h-4 w-4 mr-2" />
            Accept
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className="border-t border-border bg-card px-4 py-3 font-inter">
      <div className="flex items-center justify-between">
        {/* Call Info */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="h-5 w-5 flex items-center justify-center">
              <Phone className="h-4 w-4 text-status-success" />
            </div>
            <div>
              <Typography variant="S/500" className="font-inter text-foreground">
                Test call
              </Typography>
              <Typography variant="XS/400" className="font-inter text-secondary">
                {activeCall?.startTime ? (
                  <ElapsedTimer
                    startTime={activeCall.startTime.toISOString()}
                    format="mm:ss"
                    className="font-jetbrains text-xs-400 text-secondary tabular-nums"
                  />
                ) : (
                  'Connecting...'
                )}
              </Typography>
            </div>
          </div>
        </div>

        {/* Call Controls */}
        <div className="flex items-center gap-2">
          <Tooltip content={isMuted ? 'Unmute' : 'Mute'}>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 rounded-md border-border"
              onClick={phoneSDK.toggleMute}
              aria-label={isMuted ? 'Unmute microphone' : 'Mute microphone'}
              aria-pressed={isMuted}
              data-testid="button-mute"
            >
              {isMuted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </Button>
          </Tooltip>

          <Tooltip content="IVR Keypad">
            <Popover open={isDialpadOpen} onOpenChange={setIsDialpadOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className="h-8 w-8 rounded-md border-border"
                  aria-label="Open keypad"
                  data-testid="button-dialpad"
                >
                  <Hash className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent>
                <IVRKeypad
                  value={[]}
                  onChange={() => {}}
                  label="Send DTMF"
                  onKeySelect={handleKeySelect}
                />
              </PopoverContent>
            </Popover>
          </Tooltip>

          <Tooltip content="End Call">
            <Button
              variant="destructive"
              size="icon"
              className="h-8 w-8 rounded-md"
              onClick={phoneSDK.hangup}
              aria-label="End call"
              data-testid="button-hangup"
            >
              <PhoneOff className="h-4 w-4 text-destructive-foreground" />
            </Button>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
